/**
 * Unlimited dialer console API (LeadStoreSniper's pooled predictive dialer,
 * running on this backend). The backend wraps every response in
 * { statusCode, message, data }; the axios interceptor returns that envelope,
 * so `.data` is the payload.
 */
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

export type UnlimitedSessionStatus = "offline" | "connecting" | "ready" | "connected" | "wrapup" | "paused";

export interface UnlimitedDisposition {
  value: string;
  label: string;
  /** Extra input the wrap-up must collect. */
  needs: "callback_at" | "appointment" | null;
  /** The lead becomes the agent's own lead in their CRM. */
  claim: boolean;
}

export interface UnlimitedLeadCard {
  attemptId: string;
  bridgedAt: string | null;
  endedAt: string | null;
  lead: {
    firstName: string | null;
    lastName: string | null;
    phone: string;
    email: string | null;
    address: string | null;
    city: string | null;
    state: string;
    zip: string | null;
    dob: string | null;
    attempts: number;
    source: string | null;
    extra: Record<string, unknown>;
  };
}

export interface UnlimitedDialerState {
  /** The pacing loop is switched on for this environment. */
  enabled: boolean;
  entitlement: { active: boolean; periodEnd: string | null; states: string[] } | null;
  session: { status: UnlimitedSessionStatus; conferenceName: string | null; states: string[]; readySince: string | null } | null;
  screen: UnlimitedLeadCard | null;
  stats: { connects: number; claimed: number; talk_seconds: number };
  /** False only for a free dialer account that has bought nothing yet (no subscription, no leads). */
  crmUnlocked: boolean;
  dispositions: UnlimitedDisposition[];
}

export interface UnlimitedDispositionInput {
  attemptId: string;
  disposition: string;
  notes?: string;
  callbackAt?: string;
  appointment?: { startTime: string; endTime?: string; time?: string; date?: string; notes?: string };
  pause?: boolean;
}

const url = (path: string) => getApiUrl(`/unlimited-dialer${path}`);

async function post<T>(path: string, body: unknown = {}): Promise<T> {
  const res = await api.post<{ data: T }>({ url: url(path), body, auth: true });
  return (res as any)?.data as T;
}

export const unlimitedDialerService = {
  async state(): Promise<UnlimitedDialerState> {
    const res = await api.get<{ data: UnlimitedDialerState }>({ url: url("/state"), auth: true });
    return (res as any)?.data as UnlimitedDialerState;
  },
  start: () => post<{ conferenceName: string; dialTo: string; states: string[] }>("/start"),
  ready: () => post<{ ok: true }>("/ready"),
  pause: () => post<{ ok: true }>("/pause"),
  stop: () => post<{ ok: true }>("/stop"),
  heartbeat: () => post<{ ok: true }>("/heartbeat"),
  hangup: () => post<{ ok: true }>("/hangup"),
  dispose: (input: UnlimitedDispositionInput) => post<{ claimedLeadId: string | null }>("/disposition", input),
};

/** The message a failed console call carries (the backend sends { error, message }). */
export function dialerErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string | { message?: string } } }; data?: { message?: string }; message?: string };
  const m = e?.response?.data?.message ?? e?.data?.message ?? e?.message;
  if (m && typeof m === "object") return (m as { message?: string }).message || "Something went wrong.";
  return (m as string) || "Something went wrong.";
}
